import { useQuery } from '@tanstack/react-query';

import { getTaskEndPoint } from '../../common/api.endPoints';
import { Resource } from '../../common/constants/resource';
import { TaskState } from '../../common/constants/states';
import { HttpService } from '../../common/http.service';
import { Document } from '../tasks/types/document';
import { TaskTypes } from '../tasks/types/taskTypes';

interface EvidenceTask {
  id: number | string;
  taskType: TaskTypes;
  state: TaskState;
  createdAt?: string;
  updatedAt?: string;
  taskData?: {
    isNativeEti?: boolean;
  };
  evidence?: {
    documents?: Document[];
  };
}

export type EvidenceDetails =
  | {
      kind: 'document';
      label: string;
      uri: string;
      fileName: string;
    }
  | {
      kind: 'link';
      label: string;
      href: string;
    };

export interface EvidenceOrderedRow {
  id: number;
  taskType: TaskTypes;
  state: TaskState;
  receivedOn?: string;
  details?: EvidenceDetails;
}

const TaskStateLabel: Record<TaskState, string> = {
  [TaskState.REQUESTED]: 'Requested',
  [TaskState.DEFAULT]: 'Not Ordered',
  [TaskState.ORDERED]: 'Ordered',
  [TaskState.FOR_REVIEW]: 'For Review',
  [TaskState.ERROR]: 'Error',
  [TaskState.COMPLETED]: 'Received',
  [TaskState.WAIVED]: 'Waived',
};

export const formatTaskStateLabel = (state: TaskState): string =>
  TaskStateLabel[state] ?? state;

export const formatCoverage = (value?: number): string =>
  value != null ? `$${value.toLocaleString()}` : Resource.NORA_M_DASH;

const ETI_RECEIVED_STATES = new Set<TaskState>([
  TaskState.FOR_REVIEW,
  TaskState.COMPLETED,
]);

const getLatestDocument = (task: EvidenceTask): Document | undefined => {
  const documents = (task.evidence?.documents ?? []).filter(
    document => !document.deleted,
  );

  return documents.reduce<Document | undefined>(
    (latest, document) =>
      document.createdAt &&
      (!latest || new Date(document.createdAt) > new Date(latest.createdAt))
        ? document
        : latest,
    undefined,
  );
};

type EvidenceTaskPresentation = Pick<
  EvidenceOrderedRow,
  'receivedOn' | 'details'
>;
type EvidenceTaskMapper = (task: EvidenceTask) => EvidenceTaskPresentation;

const mapDocumentTask: EvidenceTaskMapper = task => {
  const document = getLatestDocument(task);
  return {
    receivedOn: document?.createdAt,
    details:
      document?.uri && document.fileName
        ? {
            kind: 'document',
            label: 'View document',
            uri: document.uri,
            fileName: document.fileName,
          }
        : undefined,
  };
};

const mapEtiTask: EvidenceTaskMapper = task => {
  if (!ETI_RECEIVED_STATES.has(task.state)) {
    return {};
  }
  return {
    receivedOn: task.updatedAt,
    details: task.taskData?.isNativeEti
      ? {
          kind: 'link',
          label: 'View response',
          href: `/workbench/eti/${task.id}/answer`,
        }
      : undefined,
  };
};

// Register document-backed tasks with the shared mapper. Tasks with custom
// behavior get their own mapper, keeping additions local to this table.
const EVIDENCE_TASK_MAPPERS: Partial<Record<TaskTypes, EvidenceTaskMapper>> = {
  [TaskTypes.LAB_PIQTURE]: mapDocumentTask,
  [TaskTypes.EHR]: mapDocumentTask,
  [TaskTypes.APS]: mapDocumentTask,
  [TaskTypes.ADDITIONAL_INFORMATION_FROM_CUSTOMER]: mapDocumentTask,
  [TaskTypes.ETI]: mapEtiTask,
};

export async function getEvidenceOrdered(
  applicationId: number,
): Promise<EvidenceOrderedRow[]> {
  const response = await HttpService.getInstance().get(
    getTaskEndPoint(String(applicationId)),
  );
  if (!response || response.error) {
    return [];
  }
  const tasks: EvidenceTask[] = response.parsedBody ?? [];
  return tasks.flatMap((task): EvidenceOrderedRow[] => {
    const mapTask = EVIDENCE_TASK_MAPPERS[task.taskType];
    return mapTask
      ? [
          {
            id: Number(task.id),
            taskType: task.taskType,
            state: task.state,
            ...mapTask(task),
          },
        ]
      : [];
  });
}

export const getEvidenceOrderedQueryKey = (applicationId: number) => [
  'linkedPoliciesEvidenceOrdered',
  applicationId,
];

/**
 * Query-backed wrapper around `getEvidenceOrdered` — only fetches while
 * `enabled` (i.e. once the linked policy item is expanded). `evidenceRows`
 * stays `undefined` until the query has actually resolved, so callers can
 * tell "not fetched yet" apart from "fetched and there's no evidence".
 */
export const useEvidenceOrdered = (
  applicationId: number | undefined,
  enabled: boolean,
) => {
  const { data, isLoading } = useQuery({
    queryKey: getEvidenceOrderedQueryKey(applicationId as number),
    queryFn: () => getEvidenceOrdered(applicationId as number),
    enabled: enabled && !!applicationId,
    refetchOnWindowFocus: false,
  });

  return {
    evidenceRows: data,
    isLoading: enabled ? isLoading : false,
  };
};
